import { makeObservable, observable } from 'mobx'

import { IGeoJson2DGeographicCoordinates, IMarkup } from '~/client/graph'
import Guard from '~/client/src/shared/utils/Guard'

import { copyObject } from '../utils/util'
import BaseModel from './BaseModel'

export default class Markup extends BaseModel<IMarkup> implements IMarkup {
  public static fromDto(dto: IMarkup) {
    const markup = new Markup(
      dto.id,
      dto.projectId,
      dto.sitePermitId,
      dto.kind,
      dto.geometryType,
      (dto.coordinates as IGeoJson2DGeographicCoordinates[]) || [],
      dto.color,
      dto.strokeWidth,
      dto.opacity,
      dto.fillColor,
      dto.text,
      dto.strokeStyle,
      dto.fillStyle,
      dto.globeViewId,
      dto.createdAt,
      dto.updatedAt,
    )
    markup.shapeKind = dto.shapeKind
    markup.lineType = dto.lineType
    markup.arrowheadStart = dto.arrowheadStart
    markup.arrowheadEnd = dto.arrowheadEnd
    markup.fontSize = dto.fontSize
    markup.bold = dto.bold
    markup.italic = dto.italic
    markup.underline = dto.underline
    markup.textAlign = dto.textAlign
    markup.boxWidth = dto.boxWidth
    markup.rotation = dto.rotation
    return markup
  }

  @observable public sitePermitId: string = null
  @observable public globeViewId?: string = null
  @observable public kind: string = null
  @observable public geometryType: string = null
  @observable public coordinates: IGeoJson2DGeographicCoordinates[] = []
  @observable public color?: string = null
  @observable public strokeWidth?: number = null
  @observable public opacity?: number = null
  @observable public fillColor?: string = null
  @observable public text?: string = null
  @observable public strokeStyle?: string = null
  @observable public fillStyle?: string = null
  @observable public shapeKind?: string = null
  @observable public lineType?: string = null
  @observable public arrowheadStart?: string = null
  @observable public arrowheadEnd?: string = null
  @observable public fontSize?: number = null
  @observable public bold?: boolean = null
  @observable public italic?: boolean = null
  @observable public underline?: boolean = null
  @observable public textAlign?: string = null
  @observable public boxWidth?: number = null
  @observable public rotation?: number = null
  // Client-only: true while buffered during workflow creation (before the workflow
  // is submitted). Kept off the read-only overlay and never sent to the backend.
  @observable public isDraft: boolean = false
  public projectId: string

  public constructor(
    id: string,
    projectId: string,
    sitePermitId: string,
    kind: string,
    geometryType: string,
    coordinates: IGeoJson2DGeographicCoordinates[] = [],
    color?: string,
    strokeWidth?: number,
    opacity?: number,
    fillColor?: string,
    text?: string,
    strokeStyle?: string,
    fillStyle?: string,
    globeViewId?: string,
    createdAt: number = 0,
    updatedAt: number = 0,
  ) {
    super(id)
    this.projectId = projectId
    this.sitePermitId = sitePermitId
    this.kind = kind
    this.geometryType = geometryType
    this.coordinates = coordinates || []
    this.color = color
    this.strokeWidth = strokeWidth
    this.opacity = opacity
    this.fillColor = fillColor
    this.text = text
    this.strokeStyle = strokeStyle
    this.fillStyle = fillStyle
    this.globeViewId = globeViewId
    this.setCreatedAt(createdAt)
    this.setUpdatedAt(updatedAt)
    makeObservable(this)
    Guard.requireAll({ projectId, sitePermitId })
  }

  public copy(): Markup {
    const copy = new Markup(
      this.id,
      this.projectId,
      this.sitePermitId,
      this.kind,
      this.geometryType,
      this.coordinates?.map(c => copyObject(c)),
      this.color,
      this.strokeWidth,
      this.opacity,
      this.fillColor,
      this.text,
      this.strokeStyle,
      this.fillStyle,
      this.globeViewId,
      this.createdAt,
      this.updatedAt,
    )
    copy.shapeKind = this.shapeKind
    copy.lineType = this.lineType
    copy.arrowheadStart = this.arrowheadStart
    copy.arrowheadEnd = this.arrowheadEnd
    copy.fontSize = this.fontSize
    copy.bold = this.bold
    copy.italic = this.italic
    copy.underline = this.underline
    copy.textAlign = this.textAlign
    copy.boxWidth = this.boxWidth
    copy.rotation = this.rotation
    copy.isDraft = this.isDraft
    return copy
  }
}
